import { Formik } from 'formik';
import { FC, useEffect, useState } from 'react';
import { useTimeoutFn } from 'react-use';
import useSWR from 'swr';

import { recordChatConversion } from '@/utils/recordChatConversion';
import { CheckoutDescription } from 'src/checkout/components/CheckoutDescription/CheckoutDescription';
import styles from 'src/checkout/templates/Checkout/Forms/CheckoutForm.module.scss';
import { BUY_TYPE, FINANCE_QUOTE_REPAYMENT_FREQUENCY } from 'src/checkout/templates/Checkout/Forms/CheckoutForm.type';
import {
  FINANCE_QUOTE_CONTENT_TYPE,
  FinanceQuoteContentForm,
} from 'src/checkout/templates/Checkout/Forms/FinanceQuotesFormLegacy';
import { POLLING_TIMEOUT_MILLISECONDS } from 'src/constants/constants';
import { getQuotes } from 'src/data/FinanceApi/FinanceApi';
import { FinanceQuote } from 'src/data/FinanceApi/FinanceApi.types';
import { MICROCOPY } from 'src/data/microcopy/microcopyDictionary';
import { getMicrocopy } from 'src/stores/microcopyStore';

import {
  FinanceQuotesFormValidation,
  PersonalisedFinanceQuotesFormValues,
  PersonalizedQuoteFormProps,
} from './PersonalizedQuoteForm.type';

const initialValues: PersonalisedFinanceQuotesFormValues = {
  quoteRequestId: '',
  quoteId: [],
  buyType: BUY_TYPE.PERSONAL,
  repaymentFrequency: FINANCE_QUOTE_REPAYMENT_FREQUENCY.WEEKLY,
  isVisaHolder: false,
  loanTerm: 60,
  carPrice: 0,
  deposit: 0,
  quoteInfo: [] as any,
};

interface Props extends PersonalizedQuoteFormProps<PersonalisedFinanceQuotesFormValues> {
  onClickUpdateFinanceInfo?: () => void;
}

export const FinanceQuotesForm: FC<Props> = ({
  values,
  onSubmit,
  onBack,
  hideTitle,
  onSubmitTracking,
  onClickUpdateFinanceInfo,
}) => {
  const [isPollingQuotes, setIsPollingQuotes] = useState<boolean>(true);
  const [isSupportedByYouX, setIsSupportedByYouX] = useState<boolean>(false);
  const [quotes, setQuotes] = useState<FinanceQuote[]>([]);
  const { data: quotesData } = useSWR(isPollingQuotes ? values.quoteRequestId : undefined, getQuotes, {
    refreshInterval: 5000,
  });

  useEffect(() => {
    recordChatConversion('finance_quote_submit');
  }, []);

  // Mechanism to persist quote data even after polling is cancelled
  useEffect(() => {
    if (quotesData) {
      const youXData = quotesData.quotes.find((item) => item.financier === 'youx');
      if (youXData) {
        setIsPollingQuotes(false);
        setIsSupportedByYouX(true);
        setQuotes([]);
      } else {
        if (quotesData.status?.has_completed) {
          setIsPollingQuotes(false);
        }
        setQuotes(quotesData.quotes);
      }
    }
  }, [quotesData]);

  // Cancel the quotes polling after POLLING_TIMEOUT
  useTimeoutFn(() => {
    setIsPollingQuotes(false);
  }, POLLING_TIMEOUT_MILLISECONDS);

  const listTitle = getMicrocopy(MICROCOPY.PERSONALIZED_QUOTE_QUOTES_LIST_TITLE);
  const listDescription = getMicrocopy(MICROCOPY.PERSONALIZED_QUOTE_QUOTES_LIST_DESCRIPTION);

  return (
    <div className={styles.financeQuotes} data-testid="FinanceQuotesForm">
      {!hideTitle && <CheckoutDescription title={listTitle} description={listDescription} onBack={onBack} />}
      <Formik
        initialValues={{ ...initialValues, ...values }}
        onSubmit={async (values, formikHelpers) => {
          const quoteInfo = quotes.filter((q) => values.quoteId?.includes(q.quote_id));
          await onSubmit({ ...values, quoteInfo }, formikHelpers);
        }}
        validationSchema={FinanceQuotesFormValidation}
      >
        {(props) => {
          return (
            <FinanceQuoteContentForm
              formikProps={props}
              valuesProps={values}
              isPollingQuotes={isPollingQuotes}
              quotes={quotes}
              onSubmitTracking={onSubmitTracking}
              onClickUpdateFinanceInfo={onClickUpdateFinanceInfo}
              includeCarmaFinance={false}
              formType={FINANCE_QUOTE_CONTENT_TYPE.PERSONALISED}
              submitButtonAboveFooter
              allowMultipleQuotes
              quoteRequestId={values.quoteRequestId}
              isSupportedByYouX={isSupportedByYouX}
            />
          );
        }}
      </Formik>
    </div>
  );
};
